import type { DestinationMetadata } from '@/scopes/search/quick-search/domain/types'
import type { FilterOption } from '@/scopes/search/types'
import { useIntl } from '@/intl'
import { Filter } from '@/scopes/search/components/QuickSearchBar/Filter'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { Button } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import * as DestinationFilterPrimitive from './DestinationFilterPrimitive'

export function DestinationFilterComponent({
  disabled,
  onConfirm,
  options,
  selectedOption,
  id,
  asSheet,
  biggerBreakpoint,
  ...rest
}: {
  id: string
  asSheet?: boolean
  disabled?: boolean
  onConfirm: (selection: FilterOption<string, DestinationMetadata> | undefined) => void
  options: FilterOption<string, DestinationMetadata>[]
  selectedOption?: FilterOption<string, DestinationMetadata>
  biggerBreakpoint?: boolean
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()

  const handleClick = (selection: FilterOption<string, DestinationMetadata>) => {
    onConfirm(selection)
    setActiveItem(null)
  }

  const handleClose = () => {
    setActiveItem(null)
  }

  const onClear = () => {
    handleClose()
    onConfirm(undefined)
  }

  const defaultOptions = options.filter(o => o.metadata.type === 'Country' || o.metadata.type === 'Region')

  return (
    <Filter
      id={id}
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_destination_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_destination_title',
      })}
      onConfirm={handleClose}
      selectionFormattedText={selectedOption?.name}
      asSheet={asSheet}
      hasPadding={false}
      biggerBreakpoint={biggerBreakpoint}
      data-qa="destination-filter"
      {...rest}
    >
      <DestinationFilterPrimitive.Root
        options={options}
        defaultOptions={defaultOptions}
        onItemClick={handleClick}
      />
      <DestinationFilterPrimitive.Footer>
        <Button onClick={onClear} disabled={disabled} variant="transparentNeutral" fullWidth>
          {intl.formatMessage({ id: 'quicksearch_clear_button_text' })}
        </Button>
        <Button onClick={handleClose} disabled={disabled} fullWidth>
          {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
        </Button>
      </DestinationFilterPrimitive.Footer>
    </Filter>
  )
}
